import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertCircle } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import NotificationCenter from './NotificationCenter';

const NotificationBell: React.FC = () => {
  const { alerts, unreadCount } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  const hasCriticalAlerts = alerts.some(alert => 
    alert.severity === 'critical' && !alert.isRead
  );

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <motion.button
        onClick={handleToggle}
        className="relative p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all duration-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Bell className="w-6 h-6" />
        
        {/* Notification Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              className={`absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center rounded-full text-xs font-bold text-white ${
                hasCriticalAlerts ? 'bg-red-500' : 'bg-purple-500'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Critical Alert Indicator */}
        <AnimatePresence>
          {hasCriticalAlerts && (
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3"
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.2, 1] }}
              exit={{ scale: 0 }}
              transition={{ 
                duration: 1, 
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              <AlertCircle className="w-3 h-3 text-red-500 fill-red-500" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulse Animation for Critical Alerts */}
        {hasCriticalAlerts && (
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-red-500"
            animate={{ scale: [1, 1.5], opacity: [0.7, 0] }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
        )}
      </motion.button>

      {/* Notification Center Modal */}
      <AnimatePresence>
        {isOpen && (
          <NotificationCenter 
            isOpen={isOpen} 
            onClose={() => setIsOpen(false)} 
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationBell;